import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable, useServerTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, Input, Select, Switch, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { FileSpreadsheet, PauseCircle, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, refusalMessage, type PartGridDTO } from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import {
  PRODUCTION_ORDER_KINDS,
  PRODUCTION_ORDER_STATUSES,
  ProductionOrderClient,
  type ProductionOrderDTO,
} from "../../api/production";
import { percent, ProgressBar } from "../../components/ProgressBar";
import { CutlistDialog } from "./CutlistDialog";
import { isLate } from "./lot/lotModel";
import { NexpimView } from "../../views";
import { PartPicker } from "../manufacturing/PartPicker";
import { ProductionStatusBadge, productionKindLabel, productionStatusLabel } from "./badges";

/**
 * The plant's orders, paged in the database: production orders (Laval, a
 * product's semi-finished set) and their assembly orders (Terrebonne, one per
 * version), kanban and service lots.
 *
 * <p>Opens on the open ones. Each says how far its floor work is, whether
 * parts are missing, its priority, whether it is held, when it is due (red
 * once late) and, for an assembly order, its version and shelf.
 * "Runs only" hides SIGM's imported child projects (178k of them), which are
 * parts of a run rather than runs; the run's own page lists them.</p>
 */
/** Which orders a menu entry opens on: Laval's (production, kanban, service lots) or Terrebonne's. */
export type OrdersScope = "fabrication" | "assembly";

const KINDS: Record<OrdersScope, string[]> = {
  fabrication: ["FABRICATION", "KANBAN", "SERVICE"],
  assembly: ["ASSEMBLY"],
};

export function ProductionOrdersPage({ scope = "fabrication" }: { scope?: OrdersScope }) {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const navigate = useNavigate();
  const allowed = hasView(NexpimView.PRODUCTION_ORDERS);
  const orders = useMemo(() => new ProductionOrderClient(http), [http]);
  const [creating, setCreating] = useState(false);
  const [runsOnly, setRunsOnly] = useState(true);
  const [cutlist, setCutlist] = useState(false);
  const title = scope === "assembly"
    ? t("navigation.item.assembly-orders", "Assembly orders")
    : t("navigation.item.production-orders", "Production orders");

  const table = useServerTable<ProductionOrderDTO>({
    queryKey: ["production", "production-order", scope, runsOnly],
    fetchPage: (request) =>
      orders.find({
        ...request,
        filters: { kind: KINDS[scope], ...(request.filters ?? {}), ...(runsOnly ? { is_run: ["true"] } : {}) },
      }),
    enabled: allowed,
  });

  const cols = useMemo(() => {
    const c = defineColumns<ProductionOrderDTO>();
    return [
      c.number("number", (row) => row.number, { headerKey: "column.mo.number", filter: "range", priority: 1 }),
      c.text("product_sku", (row) => row.product_sku ?? "", { headerKey: "column.sku", priority: 1 }),
      c.text("part_number", (row) => row.part_number ?? "", { headerKey: "column.part.number", priority: 2 }),
      c.text("part_name", (row) => row.part_name ?? row.name ?? "", { headerKey: "column.part.name", priority: 3 }),
      c.custom(
        "kind",
        (row) => row.kind,
        (row) => <span className="text-[12px] text-ink-muted">{productionKindLabel(t, row.kind)}</span>,
        {
          headerKey: "lot.kind",
          filter: "multiselect",
          filterOptions: PRODUCTION_ORDER_KINDS.map((value) => ({ value, label: productionKindLabel(t, value) })),
          priority: 2,
        },
      ),
      c.text("version_code", (row) => row.version_code ?? "", { header: t("mo.version", "Version"), filter: "select", priority: 3 }),
      c.custom(
        "fabrication_order_id",
        (row) => row.fabrication_order_number ?? "",
        (row) => <span className="tabular-nums text-ink-muted">{row.fabrication_order_number ?? ""}</span>,
        { header: t("mo.fabrication-order", "Production order"), filter: "none", enableSorting: false, priority: 4 },
      ),
      c.custom(
        "status",
        (row) => row.status,
        (row) => (
          <span className="flex items-center gap-1.5">
            <ProductionStatusBadge status={row.status} />
            {row.is_on_hold && (
              <span title={row.hold_reason ?? undefined}>
                <PauseCircle className="size-4 text-warn" aria-label={t("lot.on-hold", "On hold")} />
              </span>
            )}
          </span>
        ),
        {
          headerKey: "column.status",
          filter: "multiselect",
          filterOptions: PRODUCTION_ORDER_STATUSES.map((value) => ({ value, label: productionStatusLabel(t, value) })),
          priority: 1,
        },
      ),
      c.custom(
        "progress",
        (row) => row.progress ?? 0,
        (row) =>
          (row.component_count ?? 0) > 0 ? (
            <span className="flex min-w-32 items-center gap-2">
              <ProgressBar value={row.progress} tone={(row.progress ?? 0) >= 1 ? "done" : "active"} className="w-16" />
              <span className="text-[12px] tabular-nums text-ink-muted">
                {percent(row.progress)} · {row.component_done_count ?? 0}/{row.component_count}
              </span>
            </span>
          ) : null,
        { headerKey: "lot.overview.progress", filter: "none", priority: 2 },
      ),
      c.custom(
        "missing_count",
        (row) => row.missing_count ?? 0,
        (row) =>
          (row.missing_count ?? 0) > 0 ? (
            <Badge variant="error" size="sm">
              {t("lot.list.missing", "{{n}} missing", { n: row.missing_count })}
            </Badge>
          ) : null,
        { headerKey: "lot.kpi.missing", filter: "range", priority: 3 },
      ),
      c.number("priority", (row) => row.priority, { headerKey: "lot.priority", filter: "range", priority: 3 }),
      c.number("quantity", (row) => row.quantity, { headerKey: "column.mo.quantity", filter: "range", priority: 2 }),
      c.number("quantity_produced", (row) => row.quantity_produced, { headerKey: "column.mo.produced", filter: "range", priority: 2 }),
      c.number("material_open_count", (row) => row.material_open_count, {
        headerKey: "column.mo.open-materials",
        filter: "range",
        priority: 3,
      }),
      c.text("start_date", (row) => row.start_date ?? "", { headerKey: "column.mo.start", filter: "daterange", priority: 3 }),
      c.custom(
        "due_date",
        (row) => row.due_date ?? "",
        (row) => <span className={isLate(row) ? "font-medium text-risk" : undefined}>{row.due_date ?? ""}</span>,
        { headerKey: "column.mo.due", filter: "daterange", priority: 2 },
      ),
      c.text("storage_location_code", (row) => row.storage_location_code ?? "", {
        headerKey: "lot.packing.location",
        filter: "none",
        enableSorting: false,
        priority: 4,
      }),
      c.text("particle_cut_date", (row) => row.particle_cut_date ?? "", {
        headerKey: "column.mo.particle-cut",
        filter: "daterange",
        defaultVisible: false,
      }),
      c.text("mdf_cut_date", (row) => row.mdf_cut_date ?? "", { headerKey: "column.mo.mdf-cut", filter: "daterange", defaultVisible: false }),
      c.text("arrived_laval_date", (row) => row.arrived_laval_date ?? "", {
        headerKey: "column.mo.arrived-laval",
        filter: "daterange",
        defaultVisible: false,
      }),
      c.text("warehouse_code", (row) => row.warehouse_code ?? "", { headerKey: "column.stock.warehouse", filter: "none", defaultVisible: false }),
      c.number("production_pipeline_lot_id", (row) => row.production_pipeline_lot_id, {
        headerKey: "column.mo.lot",
        filter: "none",
        defaultVisible: false,
      }),
      c.text("name", (row) => row.name ?? "", { headerKey: "column.mo.name", defaultVisible: false }),
    ];
  }, [t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={title} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        subtitle={scope === "assembly"
          ? t("mo.subtitle.assembly", "Terrebonne's orders, one per version of a production order: what has arrived, what is boxed, what is on a shelf.")
          : t("mo.subtitle", "What is being built, what it needs, and what has come off the line.")}
        actions={
          <>
            <Switch checked={runsOnly} onChange={setRunsOnly} label={t("mo.runs-only", "Runs only")} />
            {canWrite && scope === "fabrication" && (
              <Button variant="primary" onClick={() => setCreating(true)}>
                <Plus className="size-4" />
                {t("mo.new", "New order")}
              </Button>
            )}
          </>
        }
      />
      <DataTable<ProductionOrderDTO>
        data={table.rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey={scope === "assembly" ? "/assembly/orders" : "/production/orders"}
        loading={table.loading}
        server={table.mode}
        initialFilters={[{ id: "status", value: ["PLANNED", "FIRMED", "RELEASED", "IN_PROGRESS", "PRODUCED", "SHIPPING"] }]}
        actions={[
          {
            label: t("lot.cutlist-part", "Cut list for a part"),
            icon: <FileSpreadsheet className="size-4" />,
            onClick: () => setCutlist(true),
          },
        ]}
        onRowClick={(row) => void navigate({ to: "/production/orders/$orderId", params: { orderId: String(row.id) } })}
        emptyMessage={t("table.empty", "Nothing to show")}
      />
      {/* Mounted only while open, so a cancelled draft does not come back on the next "New order". */}
      {creating && <NewOrderDialog open onClose={() => setCreating(false)} />}
      {cutlist && <CutlistDialog onClose={() => setCutlist(false)} />}
    </>
  );
}

/**
 * A production order by hand: a product's semi-finished (91-), how many, and
 * where; the materials come from its active bill of materials, and its assembly
 * orders are planned with it (all to the default version: split it on its page).
 */
function NewOrderDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const navigate = useNavigate();
  const [part, setPart] = useState<PartGridDTO | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [warehouseId, setWarehouseId] = useState("");
  const [dueDate, setDueDate] = useState("");

  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    enabled: open,
    staleTime: 30 * 60_000,
  });
  const plants = (warehouses.data ?? []).filter((w) => w.is_active && w.kind === "MANUFACTURING");
  const effectiveWarehouse = warehouseId || String(plants[0]?.id ?? "");
  const amount = Number(quantity);

  const create = useMutation({
    mutationFn: () =>
      new ProductionOrderClient(http).create({
        id: 0,
        number: 0,
        parent_production_order_id: null,
        part_id: part?.id ?? 0,
        quantity: amount,
        status: "PLANNED",
        warehouse_id: Number(effectiveWarehouse),
        bom_id: null,
        production_pipeline_lot_id: null,
        name: null,
        start_date: null,
        due_date: dueDate || null,
        particle_cut_date: null,
        mdf_cut_date: null,
        arrived_laval_date: null,
        is_at_terrebonne: false,
        kind: "FABRICATION",
        floor_note: null,
        floor_comment: null,
        note: null,
      }),
    onSuccess: (order) => {
      onClose();
      if ((order.material_count ?? 0) === 0) {
        toast.message(t("mo.new.no-bom", "{{part}} has no active bill of materials, so the order has no materials yet.", { part: order.part_number }));
      }
      void navigate({ to: "/production/orders/$orderId", params: { orderId: String(order.id) } });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("mo.new", "New order")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={!part || !(amount > 0) || !effectiveWarehouse}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            {t("mo.create", "Create order")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("mo.part.semi-finished", "Semi-finished to make (91-)")} htmlFor="mo-new-part" required>
          {part ? (
            <div className="flex items-center gap-2 text-[13px]">
              <span className="font-medium">{part.number}</span>
              <span className="text-ink-muted">{part.name}</span>
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setPart(null)}>
                {t("button.change", "Change")}
              </Button>
            </div>
          ) : (
            <PartPicker onPick={setPart} placeholder={t("mo.part.search-semi-finished", "Search 91- or a SKU")} />
          )}
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("column.mo.quantity", "Quantity")} htmlFor="mo-new-quantity" required>
            <Input
              id="mo-new-quantity"
              type="number"
              step="any"
              min="0"
              value={quantity}
              invalid={quantity !== "" && !(amount > 0)}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </Field>
          <Field label={t("column.mo.due", "Due")} htmlFor="mo-new-due">
            <Input id="mo-new-due" type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
          </Field>
        </div>
        <Field label={t("mo.warehouse", "Issue from and receive into")} htmlFor="mo-new-warehouse" required>
          <Select
            id="mo-new-warehouse"
            value={effectiveWarehouse}
            options={plants.map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }))}
            onChange={(event) => setWarehouseId(event.target.value)}
          />
          {warehouses.isSuccess && plants.length === 0 && (
            <p className="text-[12px] text-risk">{t("mo.new.no-plant", "No active manufacturing warehouse to build in.")}</p>
          )}
        </Field>
        <p className="text-[12px] text-ink-muted">
          {t(
            "mo.new.hint.v2",
            "It starts as planned, with its Laval materials taken from the semi-finished's active bill of materials and its assembly orders planned (all to the default version: change the split on its page). Release it to the floor to produce.",
          )}
        </p>
        {part && Number.isFinite(amount) && amount > 0 && (
          <p className="text-[12px] text-ink-faint">
            {formatNumber(amount)} × {part.number}
          </p>
        )}
      </div>
    </Dialog>
  );
}
